import axios from "axios";
import { toast } from "sonner";
import { translateNow } from "@/i18n/dictionaries";
import { isForeground, loadingDone, loadingStart } from "@/lib/loadingBar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";
// URL absoluta de un recurso que sirve la API ("/api/…"), para <img src>
export const apiAssetUrl = (path) => `${BACKEND_URL}${path}`;

const SESSION_KEY = "colygon_session_token";
export const getToken = () => localStorage.getItem(SESSION_KEY) || null;
export const setToken = (t) => (t ? localStorage.setItem(SESSION_KEY, t) : localStorage.removeItem(SESSION_KEY));

export const api = axios.create({ baseURL: API });
api.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers["X-Session-Token"] = t;
  
  // Inyección de Tenant Override para Super Admin
  try {
    const overrideRaw = localStorage.getItem("colygon_tenant_override");
    if (overrideRaw) {
      const overrideObj = JSON.parse(overrideRaw);
      if (overrideObj && overrideObj.id) {
        cfg.headers["X-Company-Override"] = overrideObj.id;
      }
    }
  } catch (e) {
    console.error("Failed to parse tenant override", e);
  }

  // Barra de progreso: solo lo que la persona está esperando (ver
  // lib/loadingBar.js). cfg.progress true/false lo fuerza.
  if (cfg.progress === true || (cfg.progress !== false && isForeground())) {
    cfg.__progress = true;
    loadingStart();
  }
  return cfg;
});

// Fuera de React no hay useI18n: el aviso de red usa el diccionario en
// pantalla (i18n/dictionaries.js).
const networkErrorText = () => translateNow("common.network_error", "Error de conexión con el servidor");

// Fallos de red pasajeros. Al volver a la app tras un rato, el móvil reanuda
// la página y dispara las consultas pendientes antes de que el Wi-Fi o los
// datos estén listos; y si hay versión nueva, la app se recarga sola y corta
// las que estaban en marcha. Las dos cosas mostraban «Error de conexión con
// el servidor» sin que pasara nada. Ahora:
// - las lecturas (GET) se reintentan solas durante unos 10 s (un móvil que
//   lleva horas dormido puede tardar varios segundos en tener red);
// - el aviso solo sale para lo que la persona está esperando (lo que pidió
//   con un toque, la carga de la app al abrirla, o un guardado). Las
//   consultas automáticas (avisos, chat, viaje) se repiten solas y no avisan.
const RETRY_DELAYS_MS = [700, 1500, 3000, 5000];
const READ_METHODS = new Set(["get", "head", "options"]);
let leavingPage = false;
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", () => { leavingPage = true; });
  window.addEventListener("pageshow", () => { leavingPage = false; });
}
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const isOffline = () => typeof navigator !== "undefined" && navigator.onLine === false;

// El servidor solo contesta datos (JSON, PDF, CSV…), nunca una página web. Si
// a una petición a /api le llega HTML, no ha llegado al servidor: por ejemplo,
// una ruta de Vercel mal configurada sirve la app web en su lugar (pasó con
// #200: la app tomó esa página por el perfil, creyó que la persona no tenía
// PIN y al guardarlo fallaba sin explicación). Se trata como un fallo de red.
const isWebPageInsteadOfData = (response) => {
  const responseType = response.config?.responseType;
  if (responseType && responseType !== "json") return false;  // descargas, texto
  return String(response.headers?.["content-type"] || "").toLowerCase().includes("text/html");
};

const onResponseError = async (error) => {
  if (error.config?.__progress) loadingDone();
  // Descargas (responseType: "blob"): un error del servidor (p. ej. 402 por
  // impago) llega como Blob binario, así que error.response.data.detail sería
  // undefined y el toast mostraría un genérico. Convertimos el Blob JSON a
  // objeto para que el mensaje real ("Descargas en pausa…") llegue a la UI.
  if (error.response && error.response.data instanceof Blob &&
      (error.response.data.type || "").includes("json")) {
    try {
      error.response.data = JSON.parse(await error.response.data.text());
    } catch { /* no era JSON: se deja el Blob */ }
  }
  if (!error.response) {
    const cfg = error.config;
    // Cancelada a propósito o cortada porque la página se recarga: no es un fallo
    if (axios.isCancel(error) || error.code === "ERR_CANCELED" || leavingPage) {
      return Promise.reject(error);
    }
    // Lecturas: reintento con espera creciente. Sin conexión no: la app
    // tira enseguida de lo guardado (perfil, rutas) y hay chip de offline.
    const tries = cfg?.__retries || 0;
    if (cfg && !isOffline() && READ_METHODS.has((cfg.method || "get").toLowerCase())
        && tries < RETRY_DELAYS_MS.length) {
      cfg.__retries = tries + 1;
      await wait(RETRY_DELAYS_MS[tries]);
      if (!isOffline()) return api(cfg);
    }
    // - id fijo: los avisos se SUSTITUYEN en vez de apilarse.
    // - si el navegador sabe que está offline, no avisamos (ya hay chip
    //   ONLINE/OFFLINE en el dashboard).
    // - consultas automáticas en segundo plano: sin aviso, se repiten solas.
    const waitedFor = cfg?.__progress || !READ_METHODS.has((cfg?.method || "get").toLowerCase());
    if (!isOffline() && waitedFor) {
      toast.error(networkErrorText(), { id: "network-error", duration: 2500 });
    }
  } else {
    if (error.response.status === 401) {
      setToken(null);
      window.dispatchEvent(new Event("colygon_unauthorized"));
    }
    if (error.response.data && Array.isArray(error.response.data.detail)) {
      error.response.data.detail = error.response.data.detail.map(d => d.msg || "Error").join(", ");
    }
  }
  return Promise.reject(error);
};

api.interceptors.response.use(
  (response) => {
    if (isWebPageInsteadOfData(response)) {
      // Como un fallo de red (sin «response»): reintento de lecturas, perfil
      // guardado en el móvil y aviso de conexión en lo que se está esperando.
      return onResponseError(new axios.AxiosError(
        "La respuesta no viene del servidor", "ERR_BAD_RESPONSE", response.config, response.request));
    }
    if (response.config?.__progress) loadingDone();
    return response;
  },
  onResponseError,
);

// ---- Auth ----
export const requestMagic = (email, companyCode, nationalId) =>
  api.post("/auth/request-magic-link", {
    ...(email ? { email } : {}),
    ...(nationalId ? { national_id: nationalId } : {}),
    ...(companyCode ? { company_code: companyCode } : {}),
  }).then((r) => r.data);
export const verifyMagic = (token, name, fingerprint, companyCode, nationalId) =>
  api.post("/auth/verify", {
    token,
    ...(name ? { name } : {}),
    ...(fingerprint ? { fingerprint } : {}),
    ...(companyCode ? { company_code: companyCode } : {}),
    ...(nationalId ? { national_id: nationalId, dni_nif: nationalId } : {}),
  }).then((r) => r.data);
export const setPin = (pin) =>
  api.post("/auth/set-pin", { pin }).then((r) => r.data);
export const updateDni = (dni) =>
  api.post("/users/me/dni", { dni }).then((r) => r.data);
export const lookupId = (nationalId, companyCode) =>
  api.post("/auth/lookup-id", {
    national_id: nationalId,
    ...(companyCode ? { company_code: companyCode } : {}),
  }).then((r) => r.data);
export const loginWithPin = (nationalId, pin, fingerprint, companyCode) =>
  api.post("/auth/login-with-pin", {
    national_id: nationalId,
    pin,
    ...(fingerprint ? { fingerprint } : {}),
    ...(companyCode ? { company_code: companyCode } : {}),
  }).then((r) => r.data);
export const confirmDevice = (token) =>
  api.post("/auth/confirm-device", { token }).then((r) => r.data);
export const lookupCompany = (code) =>
  api.get("/companies/lookup", { params: { code } }).then((r) => r.data);
export const getInviteInfo = (token) =>
  api.get("/auth/invite-info", { params: { token } }).then((r) => r.data);
export const getAdminInviteToken = () =>
  api.get("/admin/company/invite-token").then((r) => r.data);
export const rotateAdminInviteToken = () =>
  api.post("/admin/company/invite-token/rotate").then((r) => r.data);
export const getMeetingPoints = () =>
  api.get("/meeting-points").then((r) => r.data);
export const logout = () =>
  api.post("/auth/logout").then((r) => r.data).catch(() => ({})).finally(() => {
    localStorage.removeItem("colygon_tenant_override");
    localStorage.removeItem("colygon_offline_me");
    cachedHubs = null;
  });
export const fetchMe = async () => {
  try {
    const data = await api.get("/auth/me").then((r) => r.data);
    localStorage.setItem("colygon_offline_me", JSON.stringify(data));
    return data;
  } catch (err) {
    if (!err.response) {
      // Solo un perfil de verdad: durante el fallo de #200 aquí se guardó una
      // página web (una cadena), que la app tomaba por el perfil.
      let cached = null;
      try { cached = JSON.parse(localStorage.getItem("colygon_offline_me") || "null"); } catch { /* corrupto */ }
      if (cached && typeof cached === "object" && cached.id) return cached;
    }
    throw err;
  }
};
export const getConfig = () => api.get("/config").then((r) => r.data);

// ---- CAE Monetization (Wallet + Payroll Bridge) ----
export const getWalletMe = () => api.get("/wallet/me").then((r) => r.data);
export const getWalletTransactions = () => api.get("/wallet/me/transactions").then((r) => r.data);
export const getPayrollExports = () => api.get("/admin/payroll/exports").then((r) => r.data);
export const generatePayrollExport = (period) => api.post("/admin/payroll/export", null, { params: { payroll_period: period }, responseType: 'blob' }).then((r) => r.data);

// ---- Analytics (ESG) ----
export const getEsgAnalytics = () => api.get("/analytics/esg-summary").then((r) => r.data);

// ---- Gamification ----
export const getTopRanking = (period = "all") =>
  api.get("/ranking/top", { params: { period } }).then((r) => r.data);

// ---- User ----
export const completeOnboarding = (data) => api.put("/users/me/onboarding", data).then((r) => r.data);
export const getWorkCenterOptions = () => api.get("/pmst/work-centers/options").then((r) => r.data);
export const updateMe = (p) => api.patch("/users/me", p).then((r) => r.data);
export const joinCompany = (companyCode) =>
  api.post("/users/me/join-company", { company_code: companyCode }).then((r) => r.data);
export const sendDigestPreview = () => api.post("/digest/preview").then((r) => r.data);
export const sendSmartEmailPreview = () => api.post("/digest/smart-email-preview").then((r) => r.data);
export const acceptLegal = () =>
  api.post("/users/me/legal-consent", { accepted: true }).then((r) => r.data);
// MegaPrompt: setHome sends postal_code + city (no street). Bages validator runs server-side.
export const setHome = (lat, lng, postalCode, city) =>
  api.post("/users/me/home", {
    lat,
    lng,
    ...(postalCode ? { postal_code: postalCode } : {}),
    ...(city ? { city } : {}),
  }).then((r) => r.data);
export const activateAdmin = (code) =>
  api.post("/users/me/activate-admin", { code }).then((r) => r.data);
// Historial de viajes (Perfil → Viajes): role = all | driver | passenger
export const getMyTripHistory = (role = "all") =>
  api.get("/users/me/trips", { params: { role } }).then((r) => r.data);
export const getMyStats = (period = "all") =>
  api.get("/users/me/stats", { params: { period } }).then((r) => r.data);
export const redeemVipParking = () =>
  api.post("/users/me/credits/redeem-vip").then((r) => r.data);
export const getPublicLeaderboard = ({ period = "weekly", limit = 3, offset = 0 } = {}) =>
  api.get("/leaderboard/public", { params: { period, limit, offset } }).then((r) => r.data);

// ---- RGPD / Legal ----
export const getLegalTerms = () => api.get("/legal/terms").then((r) => r.data);
export const submitConsents = (version, { privacy, legal, terms }) =>
  api.post("/users/me/consents", { version, privacy, legal, terms }).then((r) => r.data);
export const requestAccountDeletion = (reason) =>
  api.post("/users/me/deletion-request", { reason }).then((r) => r.data);
export const getMyDeletionRequest = () =>
  api.get("/users/me/deletion-request").then((r) => r.data);
// Copia de los propios datos (RGPD art. 15/20) como JSON descargable.
export const downloadMyData = async () => {
  const res = await api.get("/users/me/data-export.json", { responseType: "blob" });
  const url = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = "colygon-mis-datos.json";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};
export const adminListConsents = (companyId, version) =>
  api.get("/admin/consents", { params: { ...(companyId ? { company_id: companyId } : {}), ...(version ? { version } : {}) } }).then((r) => r.data);
export const adminListDeletionRequests = (status) =>
  api.get("/admin/deletion-requests", { params: status ? { status } : {} }).then((r) => r.data);
export const adminApproveDeletion = (id, note) =>
  api.post(`/admin/deletion-requests/${id}/approve`, { note }).then((r) => r.data);
export const adminRejectDeletion = (id, note) =>
  api.post(`/admin/deletion-requests/${id}/reject`, { note }).then((r) => r.data);

// ---- Hubs ----
let cachedHubs = null;
export const invalidateHubsCache = () => { cachedHubs = null; };
export const listHubSuggestions = async () => {
  if (cachedHubs) return cachedHubs;
  const data = await api.get("/hubs/suggestions").then((r) => r.data);
  cachedHubs = data;
  return data;
};
export const adminListHubs = (companyId, includeInactive = false) =>
  api.get("/admin/hubs", {
    params: {
      ...(companyId ? { company_id: companyId } : {}),
      include_inactive: includeInactive ? "true" : "false",
    },
  }).then((r) => r.data);
export const adminCreateHub = (payload) =>
  api.post("/admin/hubs", payload).then((r) => { invalidateHubsCache(); return r.data; });
export const adminPatchHub = (id, payload) =>
  api.patch(`/admin/hubs/${id}`, payload).then((r) => { invalidateHubsCache(); return r.data; });
export const adminDeleteHub = (id) =>
  api.delete(`/admin/hubs/${id}`).then((r) => { invalidateHubsCache(); return r.data; });

// ---- Teleworks ----
export const registerTelework = (lat, lng) =>
  api.post("/users/me/teleworks", { lat, lng }).then((r) => r.data);
export const getTeleworkToday = () =>
  api.get("/users/me/teleworks/today").then((r) => r.data);
export const listMyTeleworks = () =>
  api.get("/users/me/teleworks").then((r) => r.data);


// ---- Support Tickets ----
export const createSupportTicket = (p) => api.post("/support/ticket", p).then((r) => r.data);
export const getSupportTickets = () => api.get("/support/tickets").then((r) => r.data);
// Solicitudes de demo (portada pública → Soporte del super admin)
export const createDemoRequest = (payload) => api.post("/public/demo-requests", payload).then((r) => r.data);
export const listDemoRequests = () => api.get("/admin/demo-requests").then((r) => r.data);
export const setDemoRequestStatus = (id, status) => api.patch(`/admin/demo-requests/${id}`, { status }).then((r) => r.data);
export const replySupportTicket = (ticketId, text) => api.post(`/support/ticket/${ticketId}/reply`, { reply: text }).then(r => r.data);

// ---- Routes ----
export const listRoutes = (driverId, companyId) =>
  api.get("/routes", { params: { ...(driverId ? { driver_id: driverId } : {}), ...(companyId ? { company_id: companyId } : {}) } }).then((r) => r.data);
export const discardAiPlan = () => api.delete("/planner/current").then((r) => r.data);
export const getSmartMatch = (lang = "es") =>
  api.post("/planner/ai-matchmaking", null, { params: { lang } }).then((r) => r.data.response);
export const getSmartMatchesData = () => api.get("/planner/smart-matches").then((r) => r.data);
export const createRoute = (p) => api.post("/routes", p).then((r) => r.data);
export const joinRoute = (rid) => api.post(`/routes/${rid}/join`).then((r) => r.data);
export const startRoute = (rid, lat, lng) => api.post(`/routes/${rid}/start`, { lat, lng }).then((r) => r.data);
export const completeRoute = (rid) => api.post(`/routes/${rid}/complete`).then((r) => r.data);
export const rateDriver = (rid, body) => api.post(`/routes/${rid}/rate`, body).then((r) => r.data);
export const getMyRating = (rid) => api.get(`/routes/${rid}/my-rating`).then((r) => r.data);
export const deleteRoute = (rid) => api.delete(`/routes/${rid}`).then((r) => r.data);
export const generateRouteQR = (rid) => api.post(`/routes/${rid}/qr`).then((r) => r.data);
// Marca del conductor en "Mi viaje": mark = "pickup" | "absent"
export const markPassenger = (rid, pid, mark, lat = null, lng = null) =>
  api.post(`/routes/${rid}/passengers/${pid}/${mark}`, { lat, lng }).then((r) => r.data);
// MegaPrompt — single route fetch for ActiveTripDashboard auto-refresh.
export const getRoute = async (rid) => {
  try {
    const data = await api.get(`/routes/${rid}`).then((r) => r.data);
    localStorage.setItem(`colygon_offline_route_${rid}`, JSON.stringify(data));
    return data;
  } catch (err) {
    if (!err.response) {
      const cached = localStorage.getItem(`colygon_offline_route_${rid}`);
      if (cached) return JSON.parse(cached);
    }
    throw err;
  }
};
export const checkinRoute = (rid, qrCode, lat, lng, foto_url = null) =>
  api.post(`/routes/${rid}/checkin`, { qr_code: qrCode, lat, lng, foto_url }).then((r) => r.data);
export const getContingencyCode = (rid) => api.get(`/routes/${rid}/contingency-code`).then((r) => r.data);
export const checkinRouteContingency = (rid, code, foto_url = null) =>
  api.post(`/routes/${rid}/checkin-contingency`, { contingency_code: code, foto_url }).then((r) => r.data);
export const triggerEmergencyTransport = (rid) =>
  api.post(`/v1/routes/${rid}/trigger-emergency`).then((r) => r.data);
export const createRadarAlert = (payload) =>
  api.post(`/radar/alerts`, payload).then((r) => r.data);
export const listRadarAlerts = () =>
  api.get(`/radar/alerts`).then((r) => r.data);
export const deleteRadarAlert = (alertId) =>
  api.delete(`/radar/alerts/${alertId}`).then((r) => r.data);

// ---- Trips (Passive & 1-Tap Validation) ----
export const sendLocationHeartbeat = (tripId, payload) =>
  api.put(`/trips/${tripId}/location`, payload).then((r) => r.data);
export const confirmTrip1Tap = (tripId) =>
  api.post(`/trips/${tripId}/confirm_1tap`).then((r) => r.data);

// ---- Certificate ----
export const issueCertificate = () =>
  api.post("/users/me/certificate").then((r) => r.data);
export const verifyCertificate = (cid) =>
  api.get(`/certificates/${cid}`).then((r) => r.data);
export const certificatePdfUrl = (cid, lang) => {
  const qs = lang ? `?lang=${encodeURIComponent(lang)}` : "";
  return `${API}/certificates/${cid}/pdf${qs}`;
};
export const downloadCertificatePdf = async (cid, lang) => {
  const res = await api.get(`/certificates/${cid}/pdf`, {
    responseType: "blob",
    params: lang ? { lang } : {},
  });
  const blob = new Blob([res.data], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `colygon-cert-${cid}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Also open in new tab so user can preview
  window.open(url, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

// ---- Admin ----
const _scope = (companyId) => (companyId ? { company_id: companyId } : {});
// centerId: un centro de trabajo concreto (Ley 9/2025); sin él, la empresa
const _center = (centerId) => (centerId ? { center_id: centerId } : {});
// Incidencias de producción (super admin)
export const listIncidents = (includeResolved = false) =>
  api.get("/admin/ops/incidents", { params: { include_resolved: includeResolved } }).then((r) => r.data);
export const resolveIncident = (fp) => api.post(`/admin/ops/incidents/${fp}/resolve`).then((r) => r.data);
// Informe de KPI para imprimir: periodo, comparativa, 12 meses, centros
export const getKpiReport = ({ companyId, centerId, from, to }) =>
  api.get("/admin/kpi-report", {
    params: { ..._scope(companyId), ..._center(centerId), period_start: from, period_end: to },
  }).then((r) => r.data);
export const adminOverview = (companyId, centerId) =>
  api.get("/admin/overview", { params: { ..._scope(companyId), ..._center(centerId) } }).then((r) => r.data);
export const adminEmployees = (companyId, centerId) =>
  api.get("/admin/employees", { params: { ..._scope(companyId), ..._center(centerId) } }).then((r) => r.data);
export const adminWorkCenters = (companyId) =>
  api.get("/pmst/work-centers", { params: _scope(companyId) }).then((r) => r.data);
export const adminLeaderboard = (companyId) =>
  api.get("/admin/leaderboard", { params: _scope(companyId) }).then((r) => r.data);
export const adminTeleworks = (unverifiedOnly = false, companyId) =>
  api.get("/admin/teleworks", {
    params: { ...(unverifiedOnly ? { unverified_only: true } : {}), ..._scope(companyId) },
  }).then((r) => r.data);
export const adminSetRole = (id, role) =>
  api.post(`/admin/users/${id}/role`, { role }).then((r) => r.data);
export const adminGetFactory = (companyId) =>
  api.get("/admin/factory", { params: _scope(companyId) }).then((r) => r.data);
export const adminGetRoutingProvider = () =>
  api.get("/admin/routing").then((r) => r.data);

// ---- Iter 24: Bulk Importer + Logo Upload ----
export const adminBulkImport = (file, companyId, { dryRun = false, informed = false } = {}) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post("/admin/users/bulk-import", fd, {
    params: {
      ...(companyId ? { company_id: companyId } : {}),
      ...(dryRun ? { dry_run: true } : {}),
      ...(informed ? { informed: true } : {}),
    },
  }).then((r) => r.data);
};
// Invitaciones por email a los importados — por tandas (serverless-safe)
export const adminPendingInvites = (companyId) =>
  api.get("/admin/users/pending-invites", {
    params: companyId ? { company_id: companyId } : {},
  }).then((r) => r.data);
export const adminSendInvites = (companyId, batch = 25) =>
  api.post("/admin/users/send-invites", null, {
    params: { ...(companyId ? { company_id: companyId } : {}), batch },
  }).then((r) => r.data);
export const adminListBulkImports = (companyId) =>
  api.get("/admin/users/bulk-imports", {
    params: companyId ? { company_id: companyId } : {},
  }).then((r) => r.data);
export const adminUploadLogo = (companyId, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post(`/admin/companies/${companyId}/logo`, fd).then((r) => r.data);
};

// ---- Iter 25 (Sprint A): incentives + passes ----
export const getMyIncentives = (year, month) =>
  api.get("/users/me/incentives", {
    params: { ...(year ? { year } : {}), ...(month ? { month } : {}) },
  }).then((r) => r.data);
export const getMyPasses = () =>
  api.get("/users/me/passes").then((r) => r.data);
export const adminSetFactory = (cfg, companyId) =>
  api.post("/admin/factory", cfg, { params: _scope(companyId) }).then((r) => r.data);
export const adminDeleteUser = (id) => api.delete(`/admin/users/${id}`).then((r) => r.data);
export const adminDeleteRoute = (id) => api.delete(`/admin/routes/${id}`).then((r) => r.data);
export const adminDeleteTelework = (id) => api.delete(`/admin/teleworks/${id}`).then((r) => r.data);
export const adminPatchUser = (id, payload) => api.patch(`/admin/users/${id}`, payload).then((r) => r.data);
export const adminPatchRoute = (id, payload) => api.patch(`/admin/routes/${id}`, payload).then((r) => r.data);
export const adminPatchTelework = (id, payload) => api.patch(`/admin/teleworks/${id}`, payload).then((r) => r.data);
export const adminListRoutes = (companyId) =>
  api.get("/routes", { params: companyId ? { company_id: companyId } : {} }).then((r) => r.data);

// ---- Companies (Super Admin) ----
export const adminListCompanies = () => api.get("/admin/companies").then((r) => r.data);
// La ficha de la propia empresa (RRHH): el listado de arriba es solo super admin.
export const adminMyCompany = () => api.get("/admin/my-company").then((r) => r.data);
// Catálogo de departamentos + valores legacy en uso fuera del catálogo.
export const adminDepartments = (companyId) =>
  api.get("/admin/departments", { params: companyId ? { company_id: companyId } : {} }).then((r) => r.data);
export const adminCreateCompany = (payload) => api.post("/admin/companies", payload).then((r) => r.data);
// Empleados y responsables de RRHH por empresa (tabla del modo Plataforma).
export const adminCompaniesSummary = () => api.get("/admin/companies/summary").then((r) => r.data);
export const adminPatchCompany = (id, payload) => api.patch(`/admin/companies/${id}`, payload).then((r) => r.data);
export const adminDeleteCompany = (id, purge = false) =>
  api.delete(`/admin/companies/${id}`, { params: purge ? { purge: true } : {} }).then((r) => r.data);
export const adminSearchPlaces = (query) => api.get("/admin/places/search", { params: { q: query } }).then((r) => r.data);

// ---- Rewards Catalogue (HR + Super) ----
export const listRewards = () => api.get("/rewards").then((r) => r.data);
export const adminListRewards = (companyId) =>
  api.get("/admin/rewards", { params: companyId ? { company_id: companyId } : {} }).then((r) => r.data);
export const adminCreateReward = (payload) => api.post("/admin/rewards", payload).then((r) => r.data);
export const adminPatchReward = (id, payload) => api.patch(`/admin/rewards/${id}`, payload).then((r) => r.data);
export const adminDeleteReward = (id) => api.delete(`/admin/rewards/${id}`).then((r) => r.data);
export const redeemReward = (id) => api.post(`/rewards/${id}/redeem`).then((r) => r.data);

// ---- Chat ----
export const listMessages = (routeId) =>
  api.get(`/routes/${routeId}/messages`).then((r) => r.data);
export const postMessage = (routeId, payload) =>
  api.post(`/routes/${routeId}/messages`, payload).then((r) => r.data);
export const chatWebSocketUrl = (routeId) => {
  const t = getToken();
  if (!t) return null;
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  const base = BACKEND_URL ? BACKEND_URL.replace(/^http/i, "ws") : `${protocol}//${window.location.host}`;
  return `${base}/api/ws/chat/${routeId}?token=${encodeURIComponent(t)}`;
};

// ---- Security audit (HR Manager / Super Admin) ----
export const adminFingerprintEvents = (companyId, limit = 100) =>
  api.get("/admin/security/fingerprint-events", {
    params: { ...(companyId ? { company_id: companyId } : {}), limit },
  }).then((r) => r.data);

// ---- Backup & Disaster Recovery ----
export const adminListBackups = () => api.get("/admin/backup/list").then((r) => r.data);

export const adminDownloadCompanyZip = async (companyId) => {
  const res = await api.get("/admin/backup/export-company.zip", {
    responseType: "blob",
    params: companyId ? { company_id: companyId } : {},
  });
  const blob = new Blob([res.data], { type: "application/zip" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `colygon-export-${Date.now()}.zip`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
};

export const adminDownloadSnapshot = async () => {
  const res = await api.get("/admin/backup/snapshot.json", { responseType: "blob" });
  const blob = new Blob([res.data], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `colygon-snapshot-${Date.now()}.json`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
};

export const adminDownloadArchive = async (archiveId) => {
  const res = await api.get(`/admin/backup/${archiveId}/download`, { responseType: "blob" });
  const blob = new Blob([res.data], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `colygon-archive-${archiveId}.json`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
};

export const adminImportSnapshot = (file, mergeStrategy = "upsert") => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("merge_strategy", mergeStrategy);
  return api.post("/admin/backup/import", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  }).then((r) => r.data);
};

export const adminRestoreArchive = (archiveId, mergeStrategy, confirm) =>
  api.post(`/admin/backup/${archiveId}/restore`, { merge_strategy: mergeStrategy, confirm }).then((r) => r.data);

export const adminResetDatabase = (securityKey, confirm) =>
  api.post("/admin/backup/reset", { security_key: securityKey, confirm }).then((r) => r.data);

export const downloadAdminCertificate = async (companyId, lang, startDate, endDate, auditorName, reportPurpose) => {
  try {
    const params = _scope(companyId);
    if (lang) params.lang = lang;
    if (startDate) params.period_start = startDate;
    if (endDate) params.period_end = endDate;
    if (auditorName) params.auditor_name = auditorName;
    if (reportPurpose) params.report_purpose = reportPurpose;
    const res = await api.get(`/admin/certificate.pdf`, {
      responseType: "blob",
      params,
    });
    if (!res?.data || res.data.size === 0) {
      throw new Error("Empty response");
    }
    const blob = new Blob([res.data], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const filename = `colygon-corp-cert-${new Date().toISOString().slice(0, 10)}.pdf`;
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    const clickEvt = new MouseEvent("click", { view: window, bubbles: true, cancelable: true });
    const clicked = a.dispatchEvent(clickEvt);
    a.remove();
    if (!clicked || window.self !== window.top) {
      window.open(url, "_blank");
    } else {
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    }
    return true;
  } catch (err) {
    throw err;
  }
};

export const downloadAdminReport = async (kind, companyId, lang, startDate, endDate, auditorName, reportPurpose, includeEvidence, centerId) => {
  try {
    const params = { ..._scope(companyId), ..._center(centerId) };
    if (lang) params.lang = lang;
    if (startDate) params.period_start = startDate;
    if (endDate) params.period_end = endDate;
    if (auditorName) params.auditor_name = auditorName;
    if (reportPurpose) params.report_purpose = reportPurpose;
    if (includeEvidence && kind === "pdf") params.include_evidence = true;
    const res = await api.get(`/admin/report.${kind}`, {
      responseType: "blob",
      params,
    });
    if (!res?.data || res.data.size === 0) {
      throw new Error("Empty response");
    }
    const blob = new Blob([res.data], {
      type: kind === "csv" ? "text/csv;charset=utf-8" : "application/pdf",
    });
    const url = URL.createObjectURL(blob);
    const filename = `colygon-report-${new Date().toISOString().slice(0, 10)}.${kind}`;
    // Iframe-aware: try the standard <a download> first; if we're inside a
    // sandboxed App Preview iframe, the click is silently blocked → fall back
    // to window.open which the browser treats as a top-level navigation and
    // serves the PDF in a real tab the user can save from.
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Always also pop a real window so the user gets a guaranteed visible
    // result even when the inline download is blocked. Detected via referrer
    // = empty inside iframes — we just always do it for safety.
    const winLike = window.open(url, "_blank", "noopener,noreferrer");
    if (!winLike) {
      // Pop-up blocked — surface a console hint; toast is owned by caller.
      console.warn("[downloadAdminReport] popup blocked; using download anchor only");
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    return true;
  } catch (err) {
    console.error("[downloadAdminReport]", err);
    throw err;
  }
};


// ---- CAE Legal Documents ----
export const downloadCaeDeclaracion = async (companyId) => {
  const params = _scope(companyId);
  const res = await api.get("/admin/cae-declaracion-jurada.pdf", { responseType: "blob", params });
  const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url; a.download = "declaracion-jurada-anexo2.pdf"; a.rel = "noopener"; a.target = "_blank";
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

export const downloadCaeConvenio = async (companyId) => {
  const params = _scope(companyId);
  const res = await api.get("/admin/cae-convenio.pdf", { responseType: "blob", params });
  const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url; a.download = "convenio-cae.pdf"; a.rel = "noopener"; a.target = "_blank";
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

// Anexo I en formato máquina para el verificador CAE (mismas filas y hash
// de integridad que el Anexo del dossier PDF).
export const downloadEvidenceCsv = async (companyId, startDate, endDate) => {
  const params = _scope(companyId);
  if (startDate) params.period_start = startDate;
  if (endDate) params.period_end = endDate;
  const res = await api.get(`/admin/evidence.csv`, { responseType: "blob", params });
  if (!res?.data || res.data.size === 0) throw new Error("Empty response");
  const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `colygon-evidencias-cae-${new Date().toISOString().slice(0, 10)}.csv`;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return true;
};

// Borrador de Plan de Movilidad al Trabajo (PMT) — módulo Asesor IA. La
// generación incluye una llamada al redactor IA, así que puede tardar ~30 s.
export const downloadPmtPdf = async (lang, centerId) => {
  const res = await api.get("/advisor/pmt.pdf", {
    responseType: "blob",
    params: { ...(lang ? { lang } : {}), ..._center(centerId) },
  });
  if (!res?.data || res.data.size === 0) throw new Error("Empty response");
  const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `colygon-pmt-borrador-${new Date().toISOString().slice(0, 10)}.pdf`;
  a.rel = "noopener";
  a.target = "_blank";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return true;
};

// Encuesta de movilidad inicial (oleada anual) — diagnóstico declarado
export const getMyMobilitySurvey = () =>
  api.get("/surveys/mobility/me").then((r) => r.data);
export const submitMobilitySurvey = (answers) =>
  api.post("/surveys/mobility", answers).then((r) => r.data);
export const adminMobilitySurveySummary = (companyId) =>
  api.get("/admin/surveys/mobility/summary", { params: _scope(companyId) }).then((r) => r.data);

// ---- Iter 25 Sprint B — Gamification, audit logs, payroll bridge ----
export const adminGamificationGet = (companyId) =>
  api.get(`/admin/companies/${companyId}/gamification`).then((r) => r.data);
export const adminGamificationPatch = (companyId, payload) =>
  api.patch(`/admin/companies/${companyId}/gamification`, payload).then((r) => r.data);
export const adminListAuditLogs = (companyId, limit = 50) =>
  api.get("/admin/audit-logs", {
    params: {
      ...(companyId ? { company_id: companyId } : {}),
      limit,
    },
  }).then((r) => r.data);
export const sendToGestoria = (companyId, year, month) =>
  api.post("/admin/payroll/send-to-gestoria", {
    company_id: companyId,
    year, month,
  }).then((r) => r.data);
export const adminGamificationImpactPreview = (companyId) =>
  api.get(`/admin/companies/${companyId}/gamification/impact-preview`).then((r) => r.data);

// ---- Iter 33 / Lote 1 — Notifications + urgent alerts ----
export const listNotifications = () =>
  api.get("/notifications").then((r) => r.data);
export const listUrgentNotifications = () =>
  api.get("/notifications/urgent").then((r) => r.data);
export const markAllNotificationsRead = () =>
  api.post("/notifications/read-all").then((r) => r.data);
export const markNotificationRead = (id) =>
  api.post(`/notifications/${id}/read`).then((r) => r.data);
export const ackUrgentNotification = (id) =>
  api.post(`/notifications/${id}/ack`).then((r) => r.data);

// ---- Lote 1.5 - Web Push Notifications (PWA) ----
export const subscribeToPush = (subscription) =>
  api.post("/push/subscribe", subscription).then((r) => r.data);
export const unsubscribeFromPush = () =>
  api.delete("/push/subscribe").then((r) => r.data);
export const getPushPublicKey = () =>
  api.get("/push/vapid-public-key").then((r) => r.data?.public_key || null);

// ---- Lote 4 — Swipe-to-Archive: dismiss completed/old routes from the feed ----
export const archiveRoute = (routeId) =>
  api.post(`/routes/${routeId}/archive`).then((r) => r.data);

// ---- Lote 2 — Payroll preview (local download, no email) ----
export const payrollPreviewCsvUrl = (companyId, startDate, endDate) =>
  `${API}/admin/payroll/preview.csv?company_id=${encodeURIComponent(companyId)}&period_start=${encodeURIComponent(startDate)}&period_end=${encodeURIComponent(endDate)}`;
export const payrollPreviewPdfUrl = (companyId, startDate, endDate) =>
  `${API}/admin/payroll/preview.pdf?company_id=${encodeURIComponent(companyId)}&period_start=${encodeURIComponent(startDate)}&period_end=${encodeURIComponent(endDate)}`;

// ---- Lote 2 — Device audit manual override ----
export const verifyFingerprintEvent = (eventId) =>
  api.post(`/admin/security/fingerprint-events/${eventId}/verify`).then((r) => r.data);
// ---- Lote 2 — Reverse geocoding via OpenStreetMap Nominatim ----
// Public endpoint, no API key, max 1 req/s. Falls back to coordinates on error.
// MegaPrompt: Only accept postcode/city-level Nominatim results.
// Types 'house' and 'street' are explicitly excluded to prevent street-level
// data being stored. viewbox + bounded=1 restrict results to Catalunya.
const GEOCODE_ALLOWED_TYPES = new Set([
  "postcode", "city", "town", "village", "municipality",
  "hamlet", "suburb", "quarter", "neighbourhood", "administrative",
]);
const GEOCODE_BLOCKED_TYPES = new Set(["house", "street", "road", "building", "attraction"]);

export const geocode = async (query) => {
  try {
    const res = await api.get("/maps/geocode", { params: { q: query } });
    const data = res.data;
    if (!data || data.length === 0) return null;

    // Pick the first result whose type is allowed (postcode/city level).
    const item = data.find((d) => {
      const t = (d.type || "").toLowerCase();
      const cls = (d.class || "").toLowerCase();
      if (GEOCODE_BLOCKED_TYPES.has(t)) return false;
      if (GEOCODE_ALLOWED_TYPES.has(t)) return true;
      // Also allow 'place' class (Nominatim uses class=place for towns/villages).
      if (cls === "place" || cls === "boundary") return true;
      return false;
    }) || data[0];

    const a = item.address || {};
    const city = a.city || a.town || a.village || a.municipality || a.county || a.state_district || "";
    const cp = a.postcode || "";
    const short = [cp, city].filter(Boolean).join(" ") || item.display_name?.split(",")[0] || "";
    return {
      lat: parseFloat(item.lat),
      lng: parseFloat(item.lon),
      address: short,
      postal_code: cp,
      city,
    };
  } catch {
    return null;
  }
};

export const reverseGeocode = async (lat, lng) => {
  try {
    const res = await api.get("/maps/reverse", { params: { lat, lng } });
    const data = res.data;
    const a = data?.address || {};
    const city = a.city || a.town || a.village || a.municipality || a.county || a.state_district || "";
    const cp = a.postcode || "";
    const short = [cp, city].filter(Boolean).join(" ") || data?.display_name?.split(",")[0] || "";
    return {
      address: data.display_name || short,
      short: short.trim() || null,
      raw: data,
    };
  } catch {
    return null;
  }
};


// ---- Super Admin — Cross-Company Analytics ----
export const getSuperAnalytics = () =>
  api.get("/admin/super/analytics").then((r) => r.data);

export const registerActiveMobility = async (lat, lng, mode = "walk") => {
  const { data } = await api.post("/users/me/active-mobility", { lat, lng, mode });
  return data;
};

export const getActiveMobilityToday = async () => {
  const { data } = await api.get("/users/me/active-mobility/today");
  return data;
};
export const getDepartmentLeagues = (companyId) =>
  api.get("/leaderboard/departments", { params: _scope(companyId) }).then((r) => r.data);

// --- Colygon League Admin ---
export const getLeagueRewards = () =>
  api.get("/admin/super/league-rewards").then((r) => r.data);

export const saveLeagueRewards = (payload) =>
  api.post("/admin/super/league-rewards", payload).then((r) => r.data);

export const uploadLeagueImage = (formData) =>
  api.post("/admin/super/upload-image", formData, {
    headers: { "Content-Type": "multipart/form-data" }
  }).then((r) => r.data);

export const getCommunityPeers = (zip) => api.get(`/community/peers?zip=${zip}`).then(r => r.data);
export const sendDoorToDoorRequest = (payload) => api.post('/community/door-request', payload).then(r => r.data);
export const acceptDoorToDoorRequest = (id) => api.post(`/community/requests/${id}/accept`).then(r => r.data);
export const rejectDoorToDoorRequest = (id) => api.post(`/community/requests/${id}/reject`).then(r => r.data);

// --- WebAuthn / Biometrics ---
export const webauthnRegisterGenerate = async () => (await api.post("/auth/webauthn/register/generate-options")).data;
export const webauthnRegisterVerify = async (credential) => (await api.post("/auth/webauthn/register/verify", { credential })).data;
export const webauthnAuthGenerate = async (email) => (await api.post("/auth/webauthn/authenticate/generate-options", { email })).data;
export const webauthnAuthVerify = async (email, credential) => (await api.post("/auth/webauthn/authenticate/verify", { email, credential })).data;

// --- Email Lookup ---
export const lookupEmail = async (email) => (await api.post("/auth/lookup-email", { email })).data;

// --- Google SSO ---
export const authGoogle = async (token, inviteToken) => (await api.post("/auth/google", { token, invite_token: inviteToken })).data;

// Inicio de sesión corporativo (Microsoft Entra ID, Google Workspace)
export const getSsoProviders = async () => (await api.get("/auth/sso/providers")).data;
export const startSso = async (provider, loginHint) =>
  (await api.post("/auth/sso/start", { provider, ...(loginHint ? { login_hint: loginHint } : {}) })).data;
export const ssoCallback = async (code, state) => (await api.post("/auth/sso/callback", { code, state })).data;
export const getSsoSettings = async (companyId) =>
  (await api.get("/admin/company/sso", { params: companyId ? { company_id: companyId } : {} })).data;
export const putSsoSettings = async (body, companyId) =>
  (await api.put("/admin/company/sso", body, { params: companyId ? { company_id: companyId } : {} })).data;

// Doble factor (TOTP) para RRHH y super admin
export const mfaVerify = async (mfaToken, code) => (await api.post("/auth/mfa/verify", { mfa_token: mfaToken, code })).data;
export const mfaStatus = async () => (await api.get("/auth/mfa/status")).data;
export const mfaSetup = async () => (await api.post("/auth/mfa/setup")).data;
export const mfaEnable = async (code) => (await api.post("/auth/mfa/enable", { code })).data;
export const mfaDisable = async (code) => (await api.post("/auth/mfa/disable", { code })).data;
export const mfaNewRecoveryCodes = async (code) => (await api.post("/auth/mfa/recovery-codes", { code })).data;
export const mfaReset = async (userId) => (await api.post(`/admin/users/${userId}/mfa/reset`)).data;
export const getSecurityPolicy = async (companyId) =>
  (await api.get("/admin/company/security", { params: companyId ? { company_id: companyId } : {} })).data;
export const putSecurityPolicy = async (body, companyId) =>
  (await api.put("/admin/company/security", body, { params: companyId ? { company_id: companyId } : {} })).data;

// Contratación y cobro (Stripe)
const companyParams = (companyId) => ({ params: companyId ? { company_id: companyId } : {} });
export const getBillingPlans = async () => (await api.get("/billing/plans")).data;   // web pública
export const getAdminBillingPlans = async () => (await api.get("/admin/billing/plans")).data;
export const putBillingPlans = async (body) => (await api.put("/admin/billing/plans", body)).data;
export const getSubscription = async (companyId) => (await api.get("/admin/billing/subscription", companyParams(companyId))).data;
export const createCheckout = async (body, companyId) =>
  (await api.post("/admin/billing/checkout", body, companyParams(companyId))).data;
export const openBillingPortal = async (companyId) => (await api.post("/admin/billing/portal", null, companyParams(companyId))).data;
export const downloadPaymentsCsv = async (month) =>
  (await api.get("/admin/billing/payments.csv", { params: { month }, responseType: "blob" })).data;

// Alta online de empresas
export const signupCompany = async (body) => (await api.post("/signup/company", body)).data;
export const signupStatus = async (sessionId) => (await api.get("/signup/status", { params: { session_id: sessionId } })).data;

// Sincronización de la plantilla con el software de RRHH
const rosterParams = (companyId) => ({ params: companyId ? { company_id: companyId } : {} });
export const rosterPreview = async (file, fullRoster, companyId) => {
  const fd = new FormData();
  fd.append("file", file);
  return (await api.post("/admin/roster-sync/preview", fd, {
    params: { full_roster: fullRoster, ...(companyId ? { company_id: companyId } : {}) } })).data;
};
export const rosterApply = async (body, companyId) => (await api.post("/admin/roster-sync/apply", body, rosterParams(companyId))).data;
export const rosterRuns = async (companyId) => (await api.get("/admin/roster-sync/runs", rosterParams(companyId))).data;
export const rosterRotateKey = async (companyId) => (await api.post("/admin/roster-sync/api-key", null, rosterParams(companyId))).data;
export const rosterRevokeKey = async (companyId) => (await api.delete("/admin/roster-sync/api-key", rosterParams(companyId))).data;
